// AdminDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";

import {
  collection,
  getDocs,
  query,
  orderBy,
  updateDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import servicios from "../../data/serviciosData";
import "../../styles/AdminDashboard.css";

import TurnosPorDia from "./TurnosPorDia";
import ResumenTurnos from "./ResumenTurnos";
import Finanzas from "./Finanzas";

const SENIA = 5000;

export default function AdminDashboard() {
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(true);

  // filtros UI
  const [filtroDia, setFiltroDia] = useState("");
  const [filtroServicio, setFiltroServicio] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [search, setSearch] = useState("");
  const [chartRange, setChartRange] = useState("day");
  const [chartType, setChartType] = useState("bar"); // bar | line

  useEffect(() => {
    const fetchTurnos = async () => {
      setLoading(true);
      const q = query(collection(db, "turnos"), orderBy("timestamp", "asc"));
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // ensure precio field exists if possible (fallback to parse from servicio)
      const enhanced = data.map((t) => {
        if (typeof t.precio !== "number") {
          // try parse from serviciosData
          const [base, tipo] = (t.servicio || "").split(" — ").map(s => s?.trim());
          const serv = servicios.find(s => s.nombre === base || s.nombre === t.servicio);
          if (serv) {
            if (serv.opciones) {
              const opt = serv.opciones.find(o => o.tipo === tipo);
              if (opt) t.precio = opt.precio;
            } else if (serv.precio) t.precio = serv.precio;
          }
        }
        return t;
      });
      setTurnos(enhanced);
      setLoading(false);
    };
    fetchTurnos();
  }, []);

  const parsePrecio = (servicioStr, fallbackPrecio) => {
    if (typeof fallbackPrecio === "number") return fallbackPrecio;
    if (!servicioStr) return null;
    const [base, tipo] = servicioStr.split(" — ").map((s) => s?.trim());
    const serv = servicios.find((s) => s.nombre === (base || servicioStr));
    if (!serv) return null;
    if (!serv.opciones) return serv.precio ?? null;
    const opt = serv.opciones.find((o) => o.tipo === tipo);
    return opt ? opt.precio : null;
  };

  // filtros aplicados
  const filteredTurnos = useMemo(() => {
    return turnos
      .filter((t) => (filtroDia ? t.fecha === filtroDia : true))
      .filter((t) => (filtroServicio ? (t.servicio || "").includes(filtroServicio) : true))
      .filter((t) => (filtroEstado ? (t.estado || "pendiente") === filtroEstado : true))
      .filter((t) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          (t.nombre || "").toLowerCase().includes(q) ||
          (t.apellido || "").toLowerCase().includes(q) ||
          (t.telefono || "").toLowerCase().includes(q) ||
          (t.servicio || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        if (a.fecha !== b.fecha) return new Date(a.fecha) - new Date(b.fecha);
        return a.hora.localeCompare(b.hora);
      });
  }, [turnos, filtroDia, filtroServicio, filtroEstado, search]);

  // agrupado visible
  const visibleGrouped = useMemo(() => {
    const g = {};
    filteredTurnos.forEach((t) => {
      if (!g[t.fecha]) g[t.fecha] = [];
      g[t.fecha].push(t);
    });
    Object.keys(g).forEach((d) => g[d].sort((a, b) => a.hora.localeCompare(b.hora)));
    return g;
  }, [filteredTurnos]);

  // métricas
  const metrics = useMemo(() => {
    const todayStr = new Date().toDateString();
    let totalTurnosHoy = 0;
    let futuros = 0;
    let pasados = 0;
    let totalSeñasEsperadas = 0;
    let señasCobras = 0;
    let ingresosPorSeñas = 0;
    let sumaPrecios = 0;
    const servicioCount = {};
    const clientesSet = new Set();

    turnos.forEach((t) => {
      const precio = parsePrecio(t.servicio, t.precio);
      if (precio) sumaPrecios += precio;
      const baseName = t.servicio?.split(" — ")[0] || t.servicio;
      servicioCount[baseName] = (servicioCount[baseName] || 0) + 1;

      const turnoDate = new Date(t.fecha);
      const hoy = new Date(todayStr);
      if (turnoDate.toDateString() === hoy.toDateString()) totalTurnosHoy++;
      if (turnoDate > hoy) futuros++;
      if (turnoDate < hoy) pasados++;

      totalSeñasEsperadas += SENIA;
      if (t.pagado) {
        señasCobras += 1;
        ingresosPorSeñas += SENIA;
      }

      const clave = (t.telefono || "") || `${t.nombre || ""} ${t.apellido || ""}`;
      if (clave) clientesSet.add(clave);
    });

    const hourCount = {};
    turnos.forEach((t) => (hourCount[t.hora] = (hourCount[t.hora] || 0) + 1));
    const busiestHour = Object.keys(hourCount).reduce((best, h) => {
      if (!best) return h;
      return hourCount[h] > hourCount[best] ? h : best;
    }, null);

    const mostRequestedServices = Object.entries(servicioCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, count]) => ({ name, count }));

    return {
      totalTurnosHoy,
      futuros,
      pasados,
      totalSeñasEsperadas,
      señasCobras,
      ingresosPorSeñas,
      sumaPrecios,
      busiestHour,
      mostRequestedServices,
      clientesUnicos: clientesSet.size,
    };
  }, [turnos]);

  // últimos 5
  const ultimos5 = useMemo(() => {
    return [...turnos].sort((a, b) => (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0)).slice(0, 5);
  }, [turnos]);

  const marcarConfirmado = async (turnoId) => {
    try {
      await updateDoc(doc(db, "turnos", turnoId), { estado: "confirmado", pagado: true }); // opcional: marcar pagado si ya cobraste
      // refrescar
      const snap = await getDocs(query(collection(db, "turnos"), orderBy("timestamp", "asc")));
      setTurnos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error confirmando:", err);
    }
  };

  const eliminarTurno = async (turnoId) => {
    const ok = window.confirm("¿Eliminar este turno?");
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "turnos", turnoId));
      setTurnos(prev => prev.filter(t => t.id !== turnoId));
    } catch (err) {
      console.error("Error borrando:", err);
    }
  };

  const enviarWhatsappConfirm = (t) => {
    const precio = parsePrecio(t.servicio, t.precio);
    const restante = precio ? precio - SENIA : "?";
    const telefonoClean = (t.telefono || "").replace(/\D/g, "");
    const mensaje =
      `🎉 *Tu turno está confirmado* 🎉%0A%0A` +
      `*${t.nombre} ${t.apellido}*%0A` +
      `💇 ${t.servicio}%0A` +
      `📅 ${t.fecha} — ${t.hora}%0A%0A` +
      (precio ? `💰 Total: $${precio}%0A💸 Seña: $${SENIA}%0A📉 Restante: $${restante}%0A%0A` : "") +
      `✨ Te esperamos en Shanna Biopeluquería 💖`;
    window.open(`https://wa.me/54${telefonoClean}?text=${mensaje}`, "_blank");
  };

  const fechasDisponibles = useMemo(() => {
    const set = Array.from(new Set(turnos.map(t => t.fecha))).sort((a,b) => new Date(a) - new Date(b));
    return set;
  }, [turnos]);

  return (
    <div className="adash-root">
   <header className="adash-header">
  <div className="adash-header-top">
    <h1>Admin Dashboard — Shanna Biopeluquería</h1>

    <button
      className="btn-logout"
      onClick={() => signOut(auth)}
    >
      Cerrar sesión
    </button>
  </div>

  <div className="adash-filters">
    <input
      placeholder="Buscar por nombre, teléfono o servicio"
      value={search}
      onChange={(e) => setSearch(e.target.value)}
    />

    <select value={filtroDia} onChange={(e) => setFiltroDia(e.target.value)}>
      <option value="">Filtrar por día</option>
      {fechasDisponibles.map((f) => (
        <option key={f} value={f}>{f}</option>
      ))}
    </select>

    <select value={filtroServicio} onChange={(e) => setFiltroServicio(e.target.value)}>
      <option value="">Filtrar por servicio</option>
      {servicios.map((s, i) => (
        <option key={i} value={s.nombre}>{s.nombre}</option>
      ))}
    </select>

    <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
      <option value="">Filtrar por estado</option>
      <option value="pendiente">Pendiente</option>
      <option value="confirmado">Confirmado</option>
      <option value="cancelado">Cancelado</option>
    </select>

    <div className="chart-controls-inline">
      <label>Rango:</label>
      <select value={chartRange} onChange={(e) => setChartRange(e.target.value)}>
        <option value="day">Día</option>
        <option value="week">Semana</option>
        <option value="month">Mes</option>
      </select>

      <label>Tipo:</label>
      <select value={chartType} onChange={(e) => setChartType(e.target.value)}>
        <option value="bar">Barras</option>
        <option value="line">Línea</option>
      </select>
    </div>
  </div>
</header>


      <main className="adash-main">
        <aside className="adash-widgets">
          <div className="widget">
            <h3>Turnos hoy</h3>
            <p className="big">{metrics.totalTurnosHoy}</p>
            <small>Clientes únicos: <strong>{metrics.clientesUnicos}</strong></small>
          </div>

          <div className="widget">
            <h3>Turnos futuros</h3>
            <p className="big">{metrics.futuros}</p>
          </div>

          <div className="widget">
            <h3>Señas (ingresos)</h3>
            <p className="big">${metrics.ingresosPorSeñas}</p>
            <small>Señas cobradas: <strong>{metrics.señasCobras}</strong></small>
          </div>

          <div className="widget">
            <h3>Restante aproximado</h3>
            <p className="big">${Math.max(0, Math.round(metrics.sumaPrecios - metrics.ingresosPorSeñas))}</p>
            <small>Total precios: <strong>${metrics.sumaPrecios}</strong></small>
          </div>

          <div className="widget">
            <h3>Horario pico</h3>
            <p className="big">{metrics.busiestHour || "—"}</p>
          </div>

          <div className="widget">
            <h3>Servicios top</h3>
            <ul className="mini-list">
              {metrics.mostRequestedServices.map((s) => (
                <li key={s.name}>{s.name} · <strong>{s.count}</strong></li>
              ))}
            </ul>
          </div>

          <div className="widget recent">
            <h3>Últimos 5 turnos</h3>
            <ul>
              {ultimos5.map((u) => (
                <li key={u.id}>
                  <strong>{u.nombre} {u.apellido}</strong><br />
                  <small>{u.servicio} · {u.fecha} {u.hora}</small>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <section className="adash-list">
          <div className="chart-panel">
            <TurnosPorDia turnos={turnos} range={chartRange} type={chartType} />
          </div>

          <div className="two-column">
            <ResumenTurnos
              turnos={turnos}
              senia={SENIA}
              onConfirm={marcarConfirmado}
              onWhatsapp={enviarWhatsappConfirm}
              onDelete={eliminarTurno}
              limit={5}
            />

            <Finanzas turnos={turnos} senia={SENIA} />
          </div>

          <div className="grouped-list">
            {Object.entries(visibleGrouped).slice(0, 7).map(([fecha, lista]) => (
              <div key={fecha} className="day-block">
                <h2 className="day-title">📅 {fecha} — {lista.length} turno(s)</h2>

                <div className="cards-row">
                  {lista.slice(0, 5).map((t) => {
                    const precio = parsePrecio(t.servicio, t.precio);
                    return (
                      <article className={`turno-card ${t.estado || "pendiente"}`} key={t.id}>
                        <div className="card-top">
                          <div className="time">{t.hora}</div>
                          <div className={`estado-badge ${t.estado || "pendiente"}`}>{t.estado || "pendiente"}</div>
                        </div>

                        <h3 className="client">{t.nombre} {t.apellido}</h3>
                        <p className="service">{t.servicio}</p>
                        <p className="phone">{t.telefono}</p>

                        <div className="money">
                          <div>Precio: <strong>{precio ? `$${precio}` : "—"}</strong></div>
                          <div>Seña: <strong>${SENIA}</strong></div>
                          <div>Rest: <strong>{precio ? `$${precio - SENIA}` : "—"}</strong></div>
                        </div>

                        <div className="card-actions">
                          <button className="btn-small" onClick={() => enviarWhatsappConfirm(t)}>WhatsApp</button>
                          {t.estado !== "confirmado" && (
                            <button className="btn-primary" onClick={() => marcarConfirmado(t.id)}>Marcar confirmado</button>
                          )}
                          <button className="btn-danger" onClick={() => eliminarTurno(t.id)}>Eliminar</button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            ))}
            {Object.keys(visibleGrouped).length === 0 && !loading && <p className="empty">No hay turnos que coincidan con los filtros.</p>}
            {loading && <p>Cargando...</p>}
          </div>
        </section>
      </main>
    </div>
  );
}
